/**
 * Document Intelligence — Documents Master (view).
 *
 * One category and its extraction fields, read-only. The fields are a plain
 * table rather than the paged list table: a category has a handful of fields,
 * and seeing all of them at once, in order, is the point of the screen.
 */
import { useEffect, useRef, useState } from 'react';
import { Tag } from 'sharedUi/Tag';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import PermissionGuard from '../../components/guards/PermissionGuard';
import PageSkeleton from '../../components/common/page-skeleton';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { Icon } from '../../components/common/icon';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useCanDelete, useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { extractErrorMessage } from '../../utils/error-handler';
import { documentCategoriesService } from '../../services/document-categories.service';
import { languageLabel, type DocumentCategory } from '../../types/document-intelligence.types';
import { DOCUMENT_MASTER_BASE_PATH } from './paths';

const DocumentMasterView = () => {
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'document-category',
    basePath: DOCUMENT_MASTER_BASE_PATH,
  });
  const canUpdate = useCanUpdate(JOB_NAMES.AI_MODEL_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.AI_MODEL_MASTER);
  const toast = useRef<Toast>(null);

  const [data, setData] = useState<DocumentCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useBreadcrumbTitle(data?.category);

  useEffect(() => {
    if (!isReady || !id) return;
    (async () => {
      try {
        setLoading(true);
        setData(await documentCategoriesService.getById(id));
      } catch (err) {
        setError(extractErrorMessage(err, 'Failed to load the category.'));
      } finally {
        setLoading(false);
      }
    })();
  }, [isReady, id]);

  const confirmDelete = async () => {
    if (!id) return;
    try {
      setDeleting(true);
      await documentCategoriesService.delete(id);
      setDeleteOpen(false);
      navigateToList();
    } catch (err) {
      setDeleteOpen(false);
      toast.current?.show({ severity: 'error', summary: 'Error', life: 10000,
        detail: extractErrorMessage(err, 'Failed to delete the category.') });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <PageSkeleton />;

  if (error || !data) {
    return (
      <div className="p-4 flex flex-col items-start gap-3">
        <p className="text-body text-error">{error || 'Category not found.'}</p>
        <Button label="Back to Documents Master" icon="pi pi-arrow-left" severity="secondary" outlined
          onClick={() => navigateToList()} />
      </div>
    );
  }

  const requiredCount = data.fields.filter((f) => f.required).length;
  const sensitiveCount = data.fields.filter((f) => f.isSensitive).length;

  return (
    <PermissionGuard jobName={JOB_NAMES.AI_MODEL_MASTER} resourceName="Documents Master">
      <Toast ref={toast} position="top-right" />
      <div className="p-3" data-testid="SCR-DocumentIntelligence-Master-View">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <Button aria-label="Back" icon="pi pi-arrow-left" severity="secondary" text rounded
            onClick={() => navigateToList()} className="p-0" style={{ width: '28px', height: '28px' }} />
          <span className="text-body text-ink-muted">Documents Master</span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center shrink-0 text-primary">
              <Icon name="file-text" size={22} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1 className="text-title font-semibold text-ink">{data.category}</h1>
                {data.isActive
                  ? <Tag value="Active" severity="success" />
                  : <Tag value="Inactive" severity="secondary" />}
                <Tag value={languageLabel(data.language)} severity="info" />
              </div>
              <p className="text-body-sm text-ink-muted">
                {data.fields.length} {data.fields.length === 1 ? 'field' : 'fields'} to extract · {requiredCount} required
                {sensitiveCount > 0 ? ` · ${sensitiveCount} sensitive` : ''}
              </p>
              {data.categoryDescription && (
                <p className="text-body-sm text-ink mt-1 max-w-2xl">{data.categoryDescription}</p>
              )}
            </div>
          </div>
          <div className="flex gap-1.5 sm:self-center">
            {canUpdate && (
              <Button label="Edit" icon="pi pi-pencil" size="small" onClick={() => navigateToEdit(data._id)}
                testId="DocumentMasterView.Button.Edit" />
            )}
            {canDelete && (
              <Button label="Delete" icon="pi pi-trash" severity="danger" size="small"
                onClick={() => setDeleteOpen(true)} testId="DocumentMasterView.Button.Delete" />
            )}
          </div>
        </div>

        {/* Fields */}
        <div className="bg-surface rounded-lg border border-line shadow-sm mb-3 overflow-hidden">
          <div className="px-4 py-3 border-b border-line">
            <h2 className="text-heading font-semibold text-ink">Fields to Extract</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="DocumentMasterView.Fields">
              <thead className="bg-canvas">
                <tr className="text-label text-ink-muted">
                  <th className="px-4 py-2 font-medium w-10">#</th>
                  <th className="px-4 py-2 font-medium min-w-[180px]">Field Name</th>
                  <th className="px-4 py-2 font-medium">Data Type</th>
                  <th className="px-4 py-2 font-medium">Sensitive</th>
                  <th className="px-4 py-2 font-medium min-w-[240px]">Description</th>
                  <th className="px-4 py-2 font-medium min-w-[200px]">Format</th>
                  <th className="px-4 py-2 font-medium min-w-[240px]">Validation Criteria</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data.fields.map((f, i) => (
                  <tr key={`${f.fieldName}:${i}`} className="align-top text-body-sm text-ink">
                    <td className="px-4 py-3 text-ink-muted">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <span className="font-medium">{f.fieldName}</span>
                        {f.required
                          ? <Tag value="Required" severity="warning" />
                          : <Tag value="Optional" severity="secondary" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{f.dataType}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {f.isSensitive
                        ? <Tag value="Yes" severity="danger" />
                        : <span className="text-ink-muted">No</span>}
                    </td>
                    <td className="px-4 py-3">{f.description || <span className="text-ink-muted">—</span>}</td>
                    <td className="px-4 py-3">{f.format || <span className="text-ink-muted">—</span>}</td>
                    <td className="px-4 py-3">{f.validationCriteria || <span className="text-ink-muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <AuditInfoCard createdAt={data.createdAt} updatedAt={data.updatedAt}
          createdBy={data.createdBy} updatedBy={data.updatedBy} />

        <DeleteConfirmDialog
          visible={deleteOpen}
          onCancel={() => setDeleteOpen(false)}
          onDelete={confirmDelete}
          message={`Delete the category "${data.category}"? Documents will no longer be classified into it.`}
          loading={deleting}
          testId="DocumentMasterView.Dialog.Delete"
        />
      </div>
    </PermissionGuard>
  );
};

export default DocumentMasterView;
